// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Global hover tooltip driven by [data-tip] anywhere in the document.
// Copied from patterns/tooltip.ts (canonical factory pattern), with a focus
// path added so keyboard users get the same values as mouse users.

let tip: HTMLDivElement | null = null;

function ensure(): HTMLDivElement {
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'hover-tip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
  }
  return tip;
}

function position(el: HTMLDivElement, x: number, y: number): void {
  const pad = 12;
  const rect = el.getBoundingClientRect();
  let left = x + 14;
  let top = y + 14;
  if (left + rect.width + pad > window.innerWidth) left = x - rect.width - 14;
  if (top + rect.height + pad > window.innerHeight) top = y - rect.height - 14;
  el.style.left = `${Math.max(pad, left)}px`;
  el.style.top = `${Math.max(pad, top)}px`;
}

function show(text: string, x: number, y: number): void {
  const el = ensure();
  el.textContent = text;
  el.classList.add('visible');
  position(el, x, y);
}

export function hideTooltip(): void {
  if (tip) tip.classList.remove('visible');
}

export function initTooltip(): void {
  let activeText = '';
  document.addEventListener('mouseover', (e) => {
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (!target) return;
    const text = target.getAttribute('data-tip') ?? '';
    if (!text) return;
    activeText = text;
    show(text, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mousemove', (e) => {
    if (!tip || !tip.classList.contains('visible')) return;
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (!target || target.getAttribute('data-tip') !== activeText) { hideTooltip(); return; }
    position(tip, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mouseout', (e) => {
    if ((e.target as Element)?.closest?.('[data-tip]')) hideTooltip();
  });
  // Keyboard parity: a focusable mark reveals the same numbers on Tab.
  document.addEventListener('focusin', (e) => {
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (!target) return;
    const text = target.getAttribute('data-tip') ?? '';
    if (!text) return;
    const r = target.getBoundingClientRect();
    activeText = text;
    show(text, r.left + r.width / 2, r.top);
  });
  document.addEventListener('focusout', hideTooltip);
  // A tooltip must never survive a view change or a scroll away from its mark.
  window.addEventListener('scroll', hideTooltip, { passive: true });
}
